import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { t } from 'i18next';
import { usePlugins, useGlobalPluginStore } from '@morph-mapper/plugins';
import { useStore } from '@/store';
import { fetchRecentFile, useLoadState, useNotify } from '@/hooks';
import { fetchOperatorIdByUserId, parseFile, useTemplate } from '@/api';
import { loadPersistedFile, loadPersistedSave } from '@/utils';
import { useSetupStore } from '../../../setup/store';
import { usePersistDraft } from './use-persist-draft';
import { parseTemplateCreated, useEntriesTree } from './use-template-arrival';

export const useTemplateRoute = () => {
  const { templateId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const recentFileId = searchParams.get('recentFileId');
  // Sent by AnalyzeTemplate: whether the backend just created this template.
  const templateCreated = parseTemplateCreated(
    searchParams.get('templateCreated'),
  );

  const [loadedId, hasFile, setId, setFile, setTextContent, setOperatorId] =
    useStore(({ config: c }) => [
      c.id,
      Boolean(c.file),
      c.setId,
      c.setFile,
      c.setTextContent,
      c.setOperatorId,
    ]);

  const [setCurrentStep, setSelectedTemplateId] = useSetupStore((s) => [
    s.setCurrentStep,
    s.setSelectedTemplateId,
  ]);

  const { load } = useLoadState();
  const { preConfig, hydrate } = usePlugins();
  const { error: notifyError } = useNotify();
  const navigate = useNavigate();
  const restoringRef = useRef(false);
  // The arrival (template + file) the templateCreated follow-up already ran for.
  const handledArrivalRef = useRef<string>();
  // templateCreated of an arrival that is loaded but not acted on yet. The
  // TemplateArrival child acts on it, once the template is ready.
  const [pendingArrival, setPendingArrival] = useState<boolean>();

  const isReady = hasFile && loadedId === templateId;

  const { data: template, isError } = useTemplate(isReady ? '' : templateId);
  const { withEntriesTree } = useEntriesTree(template?.save?.operatorId ?? 0);

  usePersistDraft(templateId, isReady);

  useEffect(() => {
    if (isReady) return;
    if (restoringRef.current) return;

    if (isError) {
      navigate('/', { replace: true });
      return;
    }

    if (!template) return;

    if (!template.save) {
      navigate('/', { replace: true });
      return;
    }

    const save = loadPersistedSave(templateId) ?? template.save;

    setId(templateId);
    // Sets the plugin context (domain/variant) that hydrate copies into each plugin.
    load(save);
    setSelectedTemplateId(templateId);

    const goToFileSelect = () => {
      setCurrentStep(2);
      navigate('/', { replace: true });
    };

    // recentFileId over current file > current file > file select
    const fileSource = recentFileId
      ? fetchRecentFile(template.recentFiles, recentFileId).catch((e) => {
          notifyError(t('message.error.recentFilesDownloadFailed'));
          throw e;
        })
      : loadPersistedFile(templateId);
    if (!fileSource) {
      goToFileSelect();
      return;
    }

    const ensureOperatorId = async () => {
      const { operatorId, userId } = useStore.getState().config;
      if (operatorId) return;
      if (!userId) throw new Error('No userId set');
      setOperatorId(await fetchOperatorIdByUserId(userId));
    };

    restoringRef.current = true;
    Promise.all([fileSource, preConfig(templateId), ensureOperatorId()])
      .then(async ([file, { plugins }]) => {
        // Templates created by AnalyzeTemplate have no entries tree yet. On
        // failure the user stays here (notified) instead of being redirected.
        const loaded = await withEntriesTree(save);
        if (!loaded) return;
        if (loaded !== save) load(loaded);

        try {
          hydrate(plugins);
        } catch (e) {
          notifyError(
            t('reevaluate.failed', {
              message: e instanceof Error ? e.message : String(e),
            }),
          );
        }

        const textContent = await parseFile(save.variant, file);
        useGlobalPluginStore.getState().setTextContent(textContent);
        setTextContent(textContent);
        setFile(file);

        // Drop the one-shot params before acting on them, so a refresh does
        // not run (paid) plugins again.
        if (recentFileId || templateCreated !== undefined) {
          const params = new URLSearchParams(searchParams);
          params.delete('recentFileId');
          params.delete('templateCreated');
          setSearchParams(params, { replace: true });
        }

        const arrival = `${templateId}:${recentFileId}`;
        if (
          templateCreated !== undefined &&
          handledArrivalRef.current !== arrival
        ) {
          handledArrivalRef.current = arrival;
          setPendingArrival(templateCreated);
        }
      })
      .catch(goToFileSelect)
      .finally(() => {
        restoringRef.current = false;
      });
  }, [isReady, isError, template, templateId]);

  return {
    isReady,
    pendingArrival,
    clearArrival: () => setPendingArrival(undefined),
  };
};
